import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '../api';

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | failed
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verify = async () => {
      // Paystack: ?reference=EDUVA-xxx&trxref=EDUVA-xxx
      // Flutterwave: ?status=successful&tx_ref=EDUVA-xxx&transaction_id=123456
      const reference = searchParams.get('reference') || searchParams.get('trxref') || searchParams.get('tx_ref');
      const flwStatus = searchParams.get('status');
      const transactionId = searchParams.get('transaction_id');

      if (!reference) {
        setStatus('failed');
        setMessage('No payment reference found. Please contact support.');
        return;
      }

      // Determine gateway from reference prefix or flutterwave params
      const gateway = flwStatus !== null ? 'flutterwave' : 'paystack';

      try {
        const params = new URLSearchParams({ reference, gateway });
        if (transactionId) params.set('transaction_id', transactionId);

        const res = await api.get(`/subscriptions/verify?${params.toString()}`);
        if (res.data?.status === 'paid') {
          setStatus('success');
          setMessage(res.data?.message || 'Payment successful! Your plan has been upgraded.');
          // Refresh school data in storage
          setTimeout(() => navigate('/school/subscription'), 2500);
        } else {
          setStatus('failed');
          setMessage(res.data?.message || 'Payment could not be verified.');
        }
      } catch (err) {
        setStatus('failed');
        setMessage(err?.detail || 'Verification failed. Please contact support if payment was deducted.');
      }
    };

    verify();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 w-full max-w-sm text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-14 h-14 animate-spin text-indigo-500 mx-auto mb-5" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Verifying Payment</h2>
            <p className="text-sm text-slate-500">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-5" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Payment Successful!</h2>
            <p className="text-sm text-slate-500 mb-4">{message}</p>
            <p className="text-xs text-slate-400">Redirecting to your subscription page...</p>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-5" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Payment Failed</h2>
            <p className="text-sm text-slate-500 mb-5">{message}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/school/subscription')}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors"
              >
                Back to Subscription
              </button>
              <button
                onClick={() => navigate('/school/dashboard')}
                className="px-4 py-2 text-slate-600 text-sm hover:bg-slate-100 rounded-xl transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
